import type { IFieldElem } from '@models/IFormData';
import type { IDocumentTable, TableColumn } from '@models/Forms/IForms';
import type { FormulaManager } from '@utils/managers/FormulaManager';
import { extractExternalDataSourceNames } from '@utils/externalDataSourceUtils';
import type {
    DocumentFormulaTraceRegistry,
    FormulaTraceRuleKind,
    FormulaTraceStatus,
    IFormulaTraceTarget,
} from '@/context/DocumentFormulaTraceContext';

interface IPublishFormulaTraceOptions {
    registry?: DocumentFormulaTraceRegistry;
    target?: IFormulaTraceTarget;
    source?: string;
    manager?: Pick<FormulaManager, 'GetFields' | 'GetPreparedExpression'>;
    fields: Record<string, IFieldElem>;
    watchedValues?: unknown[];
    result: unknown;
    status: FormulaTraceStatus;
    error?: string;
}

export const publishFormulaTrace = ({
    registry,
    target,
    source,
    manager,
    fields,
    watchedValues,
    result,
    status,
    error,
}: IPublishFormulaTraceOptions): void => {
    if (!registry || !target || !source) return;

    const dependencyFields = manager?.GetFields() ?? [];
    registry.upsert({
        ...target,
        source,
        preparedExpression: manager?.GetPreparedExpression(),
        result,
        dependencies: dependencyFields.map((field, index) => ({
            field,
            value: watchedValues && index in watchedValues ? watchedValues[index] : fields[field]?.value,
        })),
        externalSources: extractExternalDataSourceNames(source),
        status,
        error,
    });
};

const getTableIdentity = (table: IDocumentTable): string => table.id || table.key;
const getColumnIdentity = (column: TableColumn): string => column.id || column.key;

export const getTableColumnFormulaTraceTarget = (
    table: IDocumentTable,
    column: TableColumn,
    ruleKind: FormulaTraceRuleKind,
    row?: Record<string, unknown>,
): IFormulaTraceTarget => {
    const rawRowIdentity = row?.['|NUM'] ?? row?.id;
    const rowIdentity =
        typeof rawRowIdentity === 'string' || typeof rawRowIdentity === 'number' ? String(rawRowIdentity) : undefined;
    const rowSuffix = rowIdentity ? `:row:${rowIdentity}` : '';
    const rowName = rowIdentity ? ` / строка ${rowIdentity}` : '';

    return {
        targetId: `${getTableIdentity(table)}:${getColumnIdentity(column)}${rowSuffix}`,
        targetKind: 'tableColumn',
        targetName: `${table.name || table.key} / ${column.name || column.key}${rowName}`,
        ruleKind,
    };
};

export const getTableCellStyleFormulaTraceTarget = (
    table: IDocumentTable,
    column: TableColumn,
    cellStyle: string,
    styleIndex: number,
    row?: Record<string, unknown>,
): IFormulaTraceTarget => {
    const columnTarget = getTableColumnFormulaTraceTarget(table, column, 'style', row);
    return {
        ...columnTarget,
        targetId: `${columnTarget.targetId}:style:${styleIndex}`,
        targetName: `${columnTarget.targetName} / стиль ${cellStyle || styleIndex + 1}`,
    };
};
